import { createClient } from '@supabase/supabase-js';

const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL as string) || 'https://placeholder.supabase.co';
const supabaseAnonKey = (import.meta.env.VITE_SUPABASE_ANON_KEY as string) || 'placeholder-key';

// Check if environment variables are properly configured
if (supabaseUrl === 'https://placeholder.supabase.co' || supabaseAnonKey === 'placeholder-key') {
  console.warn('⚠️ Supabase not configured. Please update .env with your Supabase credentials.');
}

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// Database types
export interface Patient {
  id: string;
  tenant_id: string;
  unique_id?: string;
  name: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  blood_group?: string;
  gender?: 'male' | 'female' | '';
  note?: string;
  address?: string;
  emergency_contact?: string;
  medical_history?: string;
  created_at: string;
  updated_at: string;
}

export interface Doctor {
  id: string;
  tenant_id: string;
  name: string;
  email: string;
  phone?: string;
  specialization: string;
  department?: string;
  license_number?: string;
  license_no?: string;
  license_council?: string;
  license_expiry?: string;
  degrees?: string[];
  subspecialties?: string[];
  employment_type?: 'full_time' | 'part_time' | 'visiting' | 'locum';
  joining_date?: string;
  status?: 'draft' | 'pending' | 'active' | 'suspended';
  photo_url?: string;
  signature_url?: string;
  gender?: string;
  date_of_birth?: string;
  designation?: string;
  schedule?: any;
  created_at: string;
  updated_at: string;
}

export interface DoctorSchedule {
  id: string;
  tenant_id: string;
  doctor_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  max_appointments: number;
  created_at: string;
  updated_at: string;
}

export interface DoctorDocument {
  id: string;
  doctor_id: string;
  doc_type: 'license' | 'degree' | 'id' | 'signature' | 'other';
  file_url: string;
  file_name?: string;
  file_size?: number;
  created_at: string;
}

export interface DoctorPrivilege {
  doctor_id: string;
  privilege: string;
  granted_by?: string;
  granted_at: string;
}

export interface Appointment {
  id: string;
  tenant_id: string;
  patient_id: string;
  doctor_id: string;
  appointment_date: string;
  start_time: string;
  end_time: string;
  status: 'scheduled' | 'confirmed' | 'completed' | 'cancelled';
  serial_no?: number;
  notes?: string;
  referral_by?: string;
  appointment_charge: number;
  payment_type?: 'cash' | 'card' | 'bank_transfer' | 'insurance';
  payment_status: 'paid' | 'unpaid' | 'partial';
  prescription_status: 'none' | 'created' | 'issued';
  department?: string;
  contact_number?: string;
  created_at: string;
  updated_at: string;
  patient?: Patient;
  doctor?: Doctor;
}

export interface Invoice {
  id: string;
  tenant_id: string;
  patient_id: string;
  invoice_number: string;
  issue_date: string;
  due_date?: string;
  subtotal: number;
  discount_amount?: number;
  tax_rate: number;
  tax_amount: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  refund_amount?: number;
  refund_reason?: string;
  refund_date?: string;
  status: 'draft' | 'sent' | 'paid' | 'partial' | 'overdue' | 'cancelled' | 'refunded' | 'voided';
  notes?: string;
  reference_id?: string;
  reference_type?: string;
  created_by?: string;
  doctor_id?: string;
  counter_id?: string;
  created_at: string;
  updated_at: string;
  patient?: Patient;
  doctor?: Doctor;
  items?: InvoiceItem[];
  payments?: Payment[];
}

export interface InvoiceItem {
  id: string;
  tenant_id: string;
  invoice_id: string;
  description: string;
  quantity: number;
  unit_price: number;
  discount_amount?: number;
  tax_amount?: number;
  total: number;
  item_type?: 'lab' | 'radiology' | 'opd' | 'pharmacy' | 'other';
  item_id?: string;
  created_at: string;
}

export interface Payment {
  id: string;
  tenant_id: string;
  invoice_id: string;
  amount: number;
  payment_date: string;
  payment_method: 'cash' | 'card' | 'bank_transfer' | 'insurance';
  reference_number?: string;
  notes?: string;
  created_by?: string;
  counter_id?: string;
  created_at: string;
  updated_at: string;
}

export interface UserProfile {
  id: string;
  role: 'admin' | 'doctor' | 'pharmacy';
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: string;
  tenant_id: string;
  name: string;
  generic_name?: string;
  category: string;
  unit: string;
  price: number;
  created_at: string;
  updated_at: string;
}

export interface ProductBatch {
  id: string;
  tenant_id: string;
  product_id: string;
  batch_number: string;
  expiry_date: string;
  purchase_price: number;
  selling_price: number;
  initial_quantity: number;
  current_quantity: number;
  created_at: string;
  updated_at: string;
  product?: Product;
}

export interface StockMove {
  id: string;
  tenant_id: string;
  product_id: string;
  batch_id: string;
  type: 'sale' | 'return' | 'dispense' | 'adjustment';
  quantity: number;
  reference_id?: string;
  reference_type?: string;
  created_at: string;
}

export interface SalesInvoice {
  id: string;
  tenant_id: string;
  invoice_number: string;
  customer_name?: string;
  customer_phone?: string;
  subtotal: number;
  tax_rate: number;
  tax_amount: number;
  discount_amount: number;
  total: number;
  payment_method: 'cash' | 'card';
  status: 'completed' | 'returned';
  created_at: string;
  updated_at: string;
  items?: SalesItem[];
}

export interface SalesItem {
  id: string;
  tenant_id: string;
  sales_invoice_id: string;
  product_id: string;
  batch_id: string;
  quantity: number;
  unit_price: number;
  discount_amount: number;
  total: number;
  created_at: string;
  product?: Product;
  batch?: ProductBatch;
}

export interface MedicationOrder {
  id: string;
  tenant_id: string;
  patient_id: string;
  doctor_id: string;
  product_id: string;
  prescribed_quantity: number;
  dispensed_quantity: number;
  instructions?: string;
  status: 'active' | 'completed' | 'cancelled';
  created_at: string;
  updated_at: string;
  patient?: Patient;
  doctor?: Doctor;
  product?: Product;
}

export interface DispenseEvent {
  id: string;
  tenant_id: string;
  medication_order_id: string;
  patient_id: string;
  product_id: string;
  batch_id: string;
  quantity: number;
  unit_price: number;
  total_charge: number;
  dispensed_by: string;
  created_at: string;
  patient?: Patient;
  product?: Product;
  batch?: ProductBatch;
}

export interface Setting {
  id: string;
  key: string;
  value: any;
  created_at: string;
  updated_at: string;
}

export interface Receipt {
  id: string;
  tenant_id: string;
  receipt_number: string;
  receipt_type: 'standard' | 'detailed' | 'summary';
  invoice_id?: string;
  payment_id?: string;
  patient_id: string;
  amount: number;
  payment_method?: string;
  generated_by?: string;
  generated_at: string;
  printed_at?: string;
  pdf_url?: string;
  metadata?: any;
  created_at: string;
  invoice?: Invoice;
  payment?: Payment;
  patient?: Patient;
}

export interface PaymentHistory {
  id: string;
  invoice_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  reference_number?: string;
  notes?: string;
  created_at: string;
  invoice_number: string;
  patient_id: string;
  invoice_total: number;
  balance_due: number;
  invoice_status: string;
  reference_type?: string;
  reference_id?: string;
  patient_name: string;
  patient_unique_id?: string;
  patient_phone?: string;
}

export interface FinancialSummary {
  date: string;
  total_invoices: number;
  total_subtotal: number;
  total_discounts: number;
  total_tax: number;
  total_revenue: number;
  total_collected: number;
  total_outstanding: number;
  total_refunds: number;
  paid_invoices: number;
  partial_invoices: number;
  overdue_invoices: number;
  reference_type?: string;
}

export interface ReferralCommissionSummary {
  referrer_name: string;
  referrer_type?: string;
  total_referrals: number;
  total_service_amount: number;
  total_commission: number;
  paid_commission: number;
  pending_commission: number;
  paid_count: number;
  pending_count: number;
}

export interface Account {
  id: string;
  tenant_id: string;
  name: string;
  bank_name?: string;
  account_number?: string;
  address?: string;
  opening_balance: number;
  current_balance: number;
  status: 'active' | 'inactive' | 'closed';
  created_at: string;
  updated_at: string;
}

export interface TransactionHead {
  id: string;
  tenant_id: string;
  name: string;
  type: 'income' | 'expense';
  note?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}

export interface PriceListItem {
  id: string;
  tenant_id: string;
  service_type: 'lab' | 'radiology' | 'opd' | 'pharmacy' | 'other';
  service_id?: string;
  service_code?: string;
  service_name: string;
  category?: string;
  price: number;
  discount_allowed: boolean;
  effective_from: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
}


export interface BloodGroup {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface PCDoctor {
  id: string;
  tenant_id: string;
  name: string;
  email?: string;
  phone?: string;
  specialization?: string;
  hospital_name?: string;
  address?: string;
  commission_rate: number;
  commission_type: 'percent' | 'fixed';
  status: 'active' | 'inactive';
  notes?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface ReportDoctor {
  id: string;
  tenant_id: string;
  doctor_id?: string;
  report_type: string;
  can_sign: boolean;
  can_verify: boolean;
  priority: number;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  doctor?: Doctor;
}

export interface DoctorScheduleExtended {
  id: string;
  tenant_id: string;
  doctor_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  max_appointments: number;
  appointment_duration: number;
  location?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  doctor?: Doctor;
}

export interface DoctorBreak {
  id: string;
  tenant_id: string;
  doctor_id: string;
  schedule_id?: string;
  break_date?: string;
  day_of_week?: number;
  start_time: string;
  end_time: string;
  break_type: 'one_time' | 'recurring';
  reason?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  doctor?: Doctor;
  schedule?: DoctorScheduleExtended;
}

export interface DoctorHoliday {
  id: string;
  tenant_id: string;
  doctor_id?: string;
  holiday_name: string;
  start_date: string;
  end_date: string;
  is_recurring: boolean;
  holiday_type: 'personal' | 'public' | 'emergency';
  notes?: string;
  status: 'active' | 'inactive';
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  doctor?: Doctor;
}


export interface LabAnalyzerImport {
  id: string;
  tenant_id: string;
  analyzer_name: string;
  analyzer_model?: string;
  import_file_name?: string;
  import_file_url?: string;
  import_date: string;
  records_imported: number;
  records_failed: number;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  error_log?: string;
  imported_by?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface LabAnalyzerImportDetail {
  id: string;
  tenant_id: string;
  import_id: string;
  order_id?: string;
  patient_identifier?: string;
  test_code?: string;
  test_name?: string;
  result_value?: string;
  result_unit?: string;
  reference_range?: string;
  status: 'pending' | 'matched' | 'unmatched' | 'error';
  error_message?: string;
  matched_at?: string;
  created_at: string;
}

export interface LabTATMetric {
  id: string;
  tenant_id: string;
  order_id: string;
  test_id?: string;
  department?: string;
  priority?: string;
  ordered_at?: string;
  collected_at?: string;
  received_at?: string;
  processing_started_at?: string;
  processing_completed_at?: string;
  verified_at?: string;
  finalized_at?: string;
  collection_tat?: number;
  processing_tat?: number;
  verification_tat?: number;
  total_tat?: number;
  target_tat?: number;
  tat_status?: 'within_target' | 'near_breach' | 'breached';
  created_at: string;
  updated_at: string;
}

export interface LabReagent {
  id: string;
  tenant_id: string;
  name: string;
  code?: string;
  manufacturer?: string;
  catalog_number?: string;
  lot_number?: string;
  expiry_date?: string;
  storage_conditions?: string;
  quantity_in_stock: number;
  unit?: string;
  reorder_level: number;
  cost_per_unit: number;
  department?: string;
  status: 'active' | 'low_stock' | 'expired' | 'inactive';
  notes?: string;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

export interface LabReagentUsage {
  id: string;
  tenant_id: string;
  reagent_id: string;
  order_id?: string;
  test_id?: string;
  quantity_used: number;
  used_by?: string;
  used_at: string;
  notes?: string;
  created_at: string;
  reagent?: LabReagent;
}

export interface LabNotificationLog {
  id: string;
  tenant_id: string;
  order_id?: string;
  patient_id?: string;
  doctor_id?: string;
  notification_type: string;
  notification_method: string;
  recipient_type: string;
  recipient_id?: string;
  recipient_contact?: string;
  subject?: string;
  message: string;
  status: 'pending' | 'sent' | 'delivered' | 'failed' | 'read';
  sent_at?: string;
  delivered_at?: string;
  read_at?: string;
  error_message?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
}
